import { useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import {
  ReviewParticipantAction,
  UpdateComplianceProgramParticipantMutationMutation,
  UpdateComplianceProgramParticipantMutationMutationVariables,
} from "types/graphql-schema";

import getComplianceProgramProgressQuery from "@apps/programs/graphql/get-compliance-program-progress-query";
import updateComplianceProgramParticipantMutation from "@apps/programs/graphql/update-compliance-program-participant-mutation";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import Input from "@components/input/input";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";

/**
 * The per-row menu on the Progress tab: excuse one person, return them, or remove them.
 *
 * Every rule lives in the backend service, so this component only asks for the reason an
 * excuse needs and says plainly what removing does. A refusal - a closed review, somebody
 * in a calibration group - comes back as the mutation's error message.
 */
const ComplianceProgramParticipantRowActions = ({
  complianceProgramId,
  userId,
  userName,
  excused,
  excusedReason,
  recurs,
}: {
  complianceProgramId: number;
  userId: number;
  userName: string;
  excused: boolean;
  excusedReason?: string | null;
  // A recurring cycle copies its exclusions into the next one, so removing somebody here
  // keeps them out of every cycle that follows. The dialog has to say so.
  recurs: boolean;
}) => {
  const label = useLabel();
  const { noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const submittedWorkLabel =
    noun === "Survey"
      ? "Survey responses"
      : label("review", { capitalize: true, pluralize: true });
  const [dialog, setDialog] = useState<"excuse" | "remove" | null>(null);
  const [reason, setReason] = useState("");

  const [updateParticipant, { loading }] = useMutation<
    UpdateComplianceProgramParticipantMutationMutation,
    UpdateComplianceProgramParticipantMutationMutationVariables
  >(updateComplianceProgramParticipantMutation);

  const closeDialog = useCallback(() => {
    setDialog(null);
    setReason("");
  }, []);

  const run = useCallback(
    (action: ReviewParticipantAction, successTitle: string) => {
      updateParticipant({
        variables: {
          complianceProgramId,
          userId,
          action,
          reason: action === ReviewParticipantAction.Excuse ? reason : null,
        },
        onCompleted: () => {
          successNotificationVar({ title: successTitle });
          closeDialog();
        },
        // A notification, not a line in the dialog: "Add back to this review" runs straight
        // from the menu with no dialog open, and an error written into one nobody can see
        // would make a failed click look like nothing at all.
        onError: (mutationError) => {
          errorNotificationVar({
            title: "That change did not happen",
            description: mutationError.message,
          });
        },
        refetchQueries: [getComplianceProgramProgressQuery],
      });
    },
    [complianceProgramId, userId, reason, updateParticipant, closeDialog],
  );

  const options: DropdownOptionType[] = excused
    ? [
        {
          label: `Add back to this ${nounLower}`,
          onClick: () =>
            run(
              ReviewParticipantAction.AddBack,
              `${userName} is added back to this ${nounLower}`,
            ),
        },
        {
          label: `Remove from this ${nounLower}`,
          onClick: () => setDialog("remove"),
        },
      ]
    : [
        {
          label: `Excuse from this ${nounLower}`,
          onClick: () => setDialog("excuse"),
        },
        {
          label: `Remove from this ${nounLower}`,
          onClick: () => setDialog("remove"),
        },
      ];

  return (
    <>
      {dialog === "excuse" && (
        <Modal onClose={closeDialog}>
          <div
            className="flex flex-col text-sm p-6 gap-4"
            aria-label="Excuse participant dialog"
          >
            <ModalTitle onClose={closeDialog}>
              Excuse {userName} from this {nounLower}
            </ModalTitle>
            <div className="text-gray-600">
              {userName} stays on the participant list, but does not receive
              reminders and will not show as overdue in reporting. They are not
              notified of this change, and you can add them back to this{" "}
              {nounLower} at any time.
            </div>
            <Input
              label="Why are they excused?"
              name="excused-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="On parental leave until March"
            />
            <div className="flex justify-end gap-3">
              <Button
                theme={buttonTheme.text}
                onClick={closeDialog}
                text="Cancel"
              />
              <Button
                theme={buttonTheme.primary}
                disabled={loading || reason.trim().length === 0}
                onClick={() =>
                  run(
                    ReviewParticipantAction.Excuse,
                    `${userName} is excused from this ${nounLower}`,
                  )
                }
                text="Excuse"
              />
            </div>
          </div>
        </Modal>
      )}

      {dialog === "remove" && (
        <Modal onClose={closeDialog}>
          <div
            className="flex flex-col text-sm p-6 gap-4"
            aria-label="Remove participant dialog"
          >
            <ModalTitle onClose={closeDialog}>
              Remove {userName} from this {nounLower}
            </ModalTitle>
            <div className="text-gray-600 flex flex-col gap-2">
              <div>
                {userName} leaves the participant list for good. Updating
                participants will not bring them back.
              </div>
              <div>
                {submittedWorkLabel} they already submitted stay in the
                database, but they leave the reports. To put them back you have
                to change the participant rule and update participants again.
              </div>
              {recurs && (
                <div>
                  This {nounLower} recurs, and each new cycle copies its
                  exclusions. They stay out of those too.
                </div>
              )}
              {excused && excusedReason && (
                <div className="text-gray-500">
                  They are excused today: {excusedReason}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button
                theme={buttonTheme.text}
                onClick={closeDialog}
                text="Cancel"
              />
              <Button
                theme={buttonTheme.redDanger}
                disabled={loading}
                onClick={() =>
                  run(
                    ReviewParticipantAction.Remove,
                    `${userName} was removed from this ${nounLower}`,
                  )
                }
                text="Remove"
              />
            </div>
          </div>
        </Modal>
      )}

      <Dropdown
        align="end"
        options={options}
        aria-label={`${noun} participant row dropdown`}
      />
    </>
  );
};

export default ComplianceProgramParticipantRowActions;
